import { FC } from 'react';
import {
  BlueHourGlassIcon,
  GreenTickWithCircleIcon,
  OrangeHourGlassIcon,
  PartialIcon,
  PaymentProcessingIcon,
  RefundedBlueIcon,
  TimeUpIcon
} from 'assets/icons';
import { GenericTimeline, If } from 'components';
import { InvoiceStatus, InvoiceTimeline } from '../../invoices.model';
import { ChevronArrowRightIcon } from 'assets/icons';
import { getInvoiceStatus } from '../../utils';
import { TimelineActions } from '../../types';

interface InvoiceTimelineSectionProps {
  status: string;
  timelineEvents?: InvoiceTimeline[];
  onViewHistory: () => void;
  handleShareInvoice: () => void;
  handleSendReminder: () => void;
  isActionLoading: boolean;
}

const InvoiceTimelineSection: FC<InvoiceTimelineSectionProps> = ({
  status,
  timelineEvents,
  onViewHistory,
  handleShareInvoice,
  handleSendReminder,
  isActionLoading
}) => {
  const getTimelineStatus = (invoiceStatus: string) => {
    switch (invoiceStatus) {
      case InvoiceStatus.PAID:
      case InvoiceStatus.PAID_QR:
        return { className: 'bg-[#27AE601A]', Icon: GreenTickWithCircleIcon };
      case InvoiceStatus.PENDING:
        return { className: 'bg-[#FBF1DFB2]', Icon: OrangeHourGlassIcon };
      case InvoiceStatus.PROCESSING:
        return { className: 'bg-[#F7E2C080]', Icon: PaymentProcessingIcon };
      case InvoiceStatus.REFUNDED:
        return { className: 'bg-[#EC=F1F7]', Icon: RefundedBlueIcon };
      case InvoiceStatus.REFUND_INITIATED:
        return { className: 'bg-[#D8DFE580]', Icon: BlueHourGlassIcon };
      case InvoiceStatus.OVERDUE:
        return { className: 'bg-[#D959451A]', Icon: TimeUpIcon };
      case InvoiceStatus.PARTIALLY_PAID:
        return { className: 'bg-[#DEF1FF]', Icon: PartialIcon };
      case InvoiceStatus.PARTIALLY_REFUNDED:
        return { className: 'bg-[#ECF1F7]', Icon: RefundedBlueIcon };
      default:
        return { className: 'bg-[#F7E2C080]', Icon: OrangeHourGlassIcon };
    }
  };

  const handleButtonClick = (action: string) => {
    if (action === TimelineActions.SHARE_INVOICE) {
      // Handle share invoice navigation
      handleShareInvoice();
    } else if (action === TimelineActions.SEND_REMINDER) {
      handleSendReminder();
    }
  };
  const statusText = getInvoiceStatus(status as InvoiceStatus);
  const timelineStatus = getTimelineStatus(status);
  const { className: statusClassName, Icon } = timelineStatus;
  const firstFewTimelineEvents = timelineEvents?.slice(0, 3) ?? [];

  // Show both timelines for comparison
  return (
    <div className="rounded-md border-0 bg-white shadow-section">
      <div className="flex flex-col gap-3 pb-3">
        {/* Header Section */}
        <div className={`${statusClassName} flex items-center px-5 py-3`}>
          <Icon className="mr-2.5 shrink-0" width={18} height={18} />
          <div className="text-lg font-semibold text-primaryText">{statusText}</div>
        </div>
        <GenericTimeline
          events={firstFewTimelineEvents}
          onButtonClick={handleButtonClick}
          className="px-5"
          isActionLoading={isActionLoading}
        />
        <If condition={!!timelineEvents?.length}>
          <button
            className="flex cursor-pointer items-center justify-between px-5 text-sbase 
                font-semibold leading-5 text-secondary"
            onClick={onViewHistory}>
            View history
            <ChevronArrowRightIcon className="path-fill-current h-4 w-4 text-accentText" />
          </button>
        </If>
      </div>
    </div>
  );
};

export default InvoiceTimelineSection;
